import { ReactNode, useRef, useState, useCallback } from "react";
import { UserbackProvider } from "@userback/react";
import { UserbackOptions } from "@userback/widget";
import { AppConfig } from "@/infrastructure/services/config";
import { useUser } from "@/hooks";

const appConfig = AppConfig.getInstance();
const userBackToken = appConfig.USERBACK_TOKEN;

interface UserBackProviderProps {
  children?: ReactNode;
}

const IRUserBackProvider: React.FC<UserBackProviderProps> = ({ children }) => {
  const { user } = useUser();
  const [isUserbackLoaded, setIsUserbackLoaded] = useState(false);
  const observerRef = useRef<MutationObserver | null>(null);

  // Function to apply data-react-aria-top-layer to userback elements
  const applyTopLayerAttribute = useCallback(() => {
    // Apply to main userback button container
    const userbackButtonContainer = document.getElementById("userback_button_container");
    if (userbackButtonContainer) {
      userbackButtonContainer.setAttribute("data-react-aria-top-layer", "true");
    }

    // Apply to comment modal if it exists
    const userbackComment = document.querySelector(".userback-comment");
    if (userbackComment) {
      userbackComment.setAttribute("data-react-aria-top-layer", "true");
    }
  }, []);

  // Handle added nodes in mutation observer
  const handleAddedNodes = useCallback((addedNodes: NodeList) => {
    for (const node of addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        // Check if the added element is the comment modal or contains it
        if (element.classList?.contains("userback-comment")) {
          element.setAttribute("data-react-aria-top-layer", "true");
        } else if (element.querySelector?.(".userback-comment")) {
          const commentModal = element.querySelector(".userback-comment");
          if (commentModal) {
            commentModal.setAttribute("data-react-aria-top-layer", "true");
          }
        }
      }
    }
  }, []);

  // Handle mutations in observer
  const handleMutations = useCallback(
    (mutations: MutationRecord[]) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          handleAddedNodes(mutation.addedNodes);
        }
      }
    },
    [handleAddedNodes]
  );

  // Setup mutation observer to watch for userback-comment modal
  const setupCommentModalObserver = useCallback(() => {
    // Only setup if not already observing
    if (observerRef.current) {
      return;
    }

    // Check for existing comment modals first
    const existingCommentModals = document.querySelectorAll(".userback-comment");
    for (const modal of existingCommentModals) {
      if (!modal.hasAttribute("data-react-aria-top-layer")) {
        modal.setAttribute("data-react-aria-top-layer", "true");
      }
    }

    observerRef.current = new MutationObserver(handleMutations);

    // Start observing the document body for changes
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }, [handleMutations]);

  // Handle comment modal fallback checking
  const handleCommentModalFallback = useCallback(() => {
    const commentModals = document.querySelectorAll(".userback-comment");
    for (const modal of commentModals) {
      if (!modal.hasAttribute("data-react-aria-top-layer")) {
        modal.setAttribute("data-react-aria-top-layer", "true");
      }
    }
  }, []);

  // Setup fallback interval for comment modals
  const setupCommentModalFallback = useCallback(() => {
    // Only setup fallback if observer is not working
    if (observerRef.current) {
      return;
    }

    const commentCheckInterval = setInterval(handleCommentModalFallback, 1000);

    // Clear this interval after 30 seconds
    setTimeout(() => {
      clearInterval(commentCheckInterval);
    }, 30000);
  }, [handleCommentModalFallback]);

  const setReactAriaTopLayer = useCallback(async () => {
    if (!isUserbackLoaded) {
      setTimeout(() => {
        applyTopLayerAttribute();
        // Setup observer after userback is shown
        setupCommentModalObserver();

        // Additional fallback: periodically check for comment modals
        setupCommentModalFallback();
      }, 100);
    }
  }, [applyTopLayerAttribute, setupCommentModalObserver, setupCommentModalFallback, isUserbackLoaded]);

  if (!user) {
    return children;
  }

  const userBackOptions: UserbackOptions = {
    name: user.fullName,
    email: user.email,
    on_open: () => {
      setReactAriaTopLayer();
    },
    on_load: () => {
      setIsUserbackLoaded(true);
      setReactAriaTopLayer();
    },
  };

  return (
    <UserbackProvider token={userBackToken} options={userBackOptions}>
      {children}
    </UserbackProvider>
  );
};

export default IRUserBackProvider;
